import { useEffect, useState } from "react";
import { Modal } from "antd";
import { BarsOutlined, UnorderedListOutlined, ShrinkOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import { R } from "../../orderingTheme";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
} from "../../modalPrimitives";

interface Props {
  open: boolean;
  onClose: () => void;
  currentMode: "per-item" | "single";
  onConfirm: (mode: "per-item" | "single") => void;
  /** Има ли във фактурата ред за бакшиш — тогава под избора стои, че той
   *  остава отделен ред и при обобщаване. */
  hasTipLines?: boolean;
}

export const InvoiceSummarizeModal = ({
  open,
  onClose,
  currentMode,
  onConfirm,
  hasTipLines = false,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();

  const [mode, setMode] = useState<"per-item" | "single">(currentMode);

  // При отваряне започваме от текущия режим — така user-ът вижда коя е
  // активната опция и може да я смени и в двете посоки.
  useEffect(() => {
    if (!open) return;
    setMode(currentMode);
  }, [open, currentMode]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "Enter") {
        e.preventDefault();
        onConfirm(mode);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, mode, onClose, onConfirm]);

  const modalWidth = isMobile ? undefined : isTablet ? 440 : 500;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div style={{ background: R.surface }}>
        <ModalHeader
          icon={<BarsOutlined />}
          title={t("invoiceCreate.summarizeTitle")}
          subtitle={t("invoiceCreate.summarizeSubtitle")}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        <div
          style={{
            padding: isNarrow ? "14px 16px" : "18px 24px",
            background: R.bg,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <Option
            icon={<UnorderedListOutlined />}
            title={t("invoiceCreate.summarizePerItem")}
            desc={t("invoiceCreate.summarizePerItemDesc")}
            selected={mode === "per-item"}
            onClick={() => setMode("per-item")}
          />
          <Option
            icon={<ShrinkOutlined />}
            title={t("invoiceCreate.summarizeSingle")}
            desc={t("invoiceCreate.summarizeSingleDesc")}
            selected={mode === "single"}
            onClick={() => setMode("single")}
          />
          {hasTipLines ? (
            <p className="m-0 text-[12.5px] font-medium leading-[1.4] text-pos-soft">
              {t("invoiceTips.summarizeKeepsTip")}
            </p>
          ) : null}
        </div>

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          <PrimaryButton
            label={t("invoiceCreate.summarizeApply")}
            onClick={() => onConfirm(mode)}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>
    </Modal>
  );
};

function Option({
  icon,
  title,
  desc,
  selected,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: 14,
        borderRadius: 12,
        border: `1px solid ${selected ? R.brand : R.border}`,
        background: selected ? R.brandTint : R.surface,
        cursor: "pointer",
        textAlign: "left",
        transition: "background 120ms, border-color 120ms",
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: selected ? R.brand : R.brandTint,
          color: selected ? "#fff" : R.brandDeep,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: R.ink,
            letterSpacing: -0.2,
          }}
        >
          {title}
        </span>
        <span style={{ fontSize: 12.5, color: R.textSoft }}>{desc}</span>
      </span>
    </button>
  );
}

export default InvoiceSummarizeModal;
